/* eslint-disable no-restricted-properties */
const WIDGET_RESOURCES = {
    translations: 'Translations',
    templates: 'Templates',
};

const WIDGET_RESOURCES_API = {
    oneWidgetRequest: 'getWidgetResources',
    listOfWidgetsRequest: 'getListOfWidgetsResources',
    preloadResourcesByPage: 'preloadNextWidgetResources',
    getGenericResources: 'getGenericResources',
};

const basicPublicApi = {
    getWidgetResources: 'getWidgetTranslations',
    getListOfWidgetsResources: 'getListOfWidgetsTranslations',
};

module.exports = function(options) {
    const {
        getUrlService,
        getSessionScope,
        widgetsToPrefetchOnInit,
        widgetsToPrefetchByPage,
        parseStaticResources,
    } = options;
    let staticResourcePromise;
    const publicApiConfig = options.publicApiConfig || basicPublicApi;
    const hasStaticResources = options.hasStaticResources || false;
    const RESOURCE_TYPE = Odigeo._.get(WIDGET_RESOURCES, options.resourceType, WIDGET_RESOURCES.translations);
    const cache = {
        widgets: {},
        statics: {},
    };

    const parseResourceData = options.parseResourceData || function(resourceData) {
        const keys = Odigeo._.reduce(resourceData.cmsKeys, function(rawkeys, keyData) {
            rawkeys[keyData.name] = keyData.value;
            return rawkeys;
        }, {});
        return {
            keys,
        };
    };

    const getUrl = function(endPoint, queryValue, templates) {
        return getUrlService() + endPoint + '?' + queryValue + '=' + templates;
    };

    const noResource = function() {
        return Promise.resolve([]);
    };

    const emptyObject = function() {
        return Promise.resolve({});
    };

    const requestWidgetResource = function(templateName) {
        const service = `getWidget${RESOURCE_TYPE}`;
        return new Promise((resolve, reject) => {
            $.get(getUrl(service, 'widgetname', templateName)).then(resolve, reject);
        });
    };

    const requestListOfWidgetsResource = function(widgetsNameList) {
        const service = `getListOfWidgets${RESOURCE_TYPE}`;
        return widgetsNameList.length === 0 && staticResourcePromise ?
            noResource() :
            new Promise((resolve, reject) => {
                $.get(getUrl(service, 'widgetsnames', widgetsNameList.join(','))).then(resolve, reject);
            });
    };

    const getResourcePromise = function(resourceResponsePromise, widgetName) {
        return resourceResponsePromise.then(function(resourceResponse) {
            const rawResource = Odigeo._.find(resourceResponse, function(resource) {
                return widgetName === resource.name;
            });
            if (rawResource) {
                return Promise.resolve(parseResourceData(rawResource));
            } else {
                return Promise.reject(widgetName + ' resource not found.');
            }
        });
    };

    const getRequestedResourcesPromises = function(requestedWidgetsNameList, resourcesResponsePromise) {
        return Odigeo._.reduce(requestedWidgetsNameList, function(resources, widgetName) {
            resources[widgetName] = getResourcePromise(resourcesResponsePromise, widgetName);
            return resources;
        }, {});
    };

    const getResourcesToRequest = function(cachedResources, widgetsNameList) {
        return Odigeo._.filter(widgetsNameList, function(widgetName) {
            return Odigeo._.isUndefined(cachedResources[widgetName]);
        });
    };

    const buildResourcesResult = function(widgetsNameList, resourcesList) {
        return Odigeo._.reduce(widgetsNameList, function(totalResources, widgetName, index) {
            totalResources[widgetName] = resourcesList[index];
            return totalResources;
        }, {});
    };

    const getResourceListResult = function(cachedResources, widgetsNameList) {
        return Promise.all( Odigeo._.map(widgetsNameList, Odigeo._.propertyOf(cachedResources)))
            .then(function(values) {
                return buildResourcesResult(widgetsNameList, values);
            }, emptyObject);
    };

    const loadStaticResources = function(resourcesResponsePromise) {
        if (staticResourcePromise) {
            return staticResourcePromise;
        }
        if (!resourcesResponsePromise) {
            resourcesResponsePromise = requestListOfWidgetsResource([]);
        }
        staticResourcePromise = resourcesResponsePromise.then(Odigeo._.property('templates'));
        return staticResourcePromise;
    };

    const getWidgetsToPrefetch = ({flow, flowProduct, entryPoint}) => {
        return Odigeo._.get(widgetsToPrefetchOnInit, [flow, flowProduct, entryPoint], []);
    };

    const getCurrentPageName = () => Odigeo.History.getPageNameFromFragment();

    const getNextFlowProductWidgets = ({flow, flowProduct}) => {
        return Odigeo._.get(widgetsToPrefetchByPage, [flow, flowProduct, getCurrentPageName()], []);
    };


    /**
     * Request and initialize resources depending on flow, product and entry point
     * @return {Promise<Array<Object>>}
     */
    const initWidgetResourcesManager = () => {
        const resources = getWidgetsToPrefetch(getSessionScope());

        return getListOfWidgetsResources(resources);
    };

    /**
     * Service to get Resources from an specific widget
     * @param {String} widgetName
     * @return {Promise<Object>}
     */
    const getWidgetResources = (widgetName) => {
        const requestedWidgetsNameList = getResourcesToRequest(cache.widgets, [widgetName]);
        if (requestedWidgetsNameList.length > 0) {
            cache.widgets[widgetName] = requestWidgetResource(widgetName)
                .then(parseResourceData, emptyObject);
        }
        return cache.widgets[widgetName];
    };

    /**
     * Service to get resources for a list of widgets
     * @param {Array<String>} widgetsNameList
     * @return {Promise<Array<Object>>}
     */
    const getListOfWidgetsResources = (widgetsNameList) => {
        const requestedWidgetsNameList = getResourcesToRequest(cache.widgets, widgetsNameList);
        const resourcesResponsePromise = requestListOfWidgetsResource(requestedWidgetsNameList);
        if (hasStaticResources) {
            loadStaticResources(resourcesResponsePromise);
        }
        if (requestedWidgetsNameList.length > 0) {
            const property = `widget${RESOURCE_TYPE}`;
            const widgetResourceResponsePromise = resourcesResponsePromise.then(Odigeo._.property(property));
            const newWidgetResourcesPromises = getRequestedResourcesPromises(requestedWidgetsNameList, widgetResourceResponsePromise);
            Odigeo._.extend(cache.widgets, newWidgetResourcesPromises);
        }
        return getResourceListResult(cache.widgets, widgetsNameList);
    };

    /**
     * Service to preload widget resources by page.
     * @return {Promise<Array<Object>>}
     */
    const preloadNextWidgetResources = () => {
        const nextWidgetsToRequest = getNextFlowProductWidgets(getSessionScope());

        return !Odigeo._.isEmpty(nextWidgetsToRequest) ? getListOfWidgetsResources(nextWidgetsToRequest) : noResource();
    };

    /**
     * Service to get a static templates
     * @param {String[]} resourceNameList
     * @return {Promise<Array<Object>>}
     */
    const getGenericResources = (resourceNameList) => {
        const requestedWidgetsNameList = getResourcesToRequest(cache.statics, resourceNameList);
        const staticResourceResponsePromise = loadStaticResources();
        const newStaticResourcePromises = getRequestedResourcesPromises(requestedWidgetsNameList, staticResourceResponsePromise);
        Odigeo._.extend(cache.statics, newStaticResourcePromises);
        return getResourceListResult(cache.statics, resourceNameList)
            .then(parseStaticResources);
    };

    const removeWidget = (widgetsName) => {
        delete cache.widgets[widgetsName];
    };

    const getPublicAPI = () => {
        let publicAPI = {};
        const oneWidgetResource = Odigeo._.get(publicApiConfig, WIDGET_RESOURCES_API.oneWidgetRequest, 'getWidgetResources');
        const listOfWidgetsRequest = Odigeo._.get(publicApiConfig, WIDGET_RESOURCES_API.listOfWidgetsRequest, 'getListOfWidgetsResources');
        const preloadResourcesByPage = Odigeo._.get(publicApiConfig, WIDGET_RESOURCES_API.preloadResourcesByPage, 'preloadNextWidgetResources');
        const genericResources = Odigeo._.get(publicApiConfig, WIDGET_RESOURCES_API.getGenericResources, '');
        const removeWidgetResource = Odigeo._.get(publicApiConfig, WIDGET_RESOURCES_API.removeWidgetResource, 'removeWidgetResource');

        publicAPI.init = initWidgetResourcesManager;
        publicAPI[oneWidgetResource] = getWidgetResources;
        publicAPI[listOfWidgetsRequest] = getListOfWidgetsResources;
        publicAPI[preloadResourcesByPage] = preloadNextWidgetResources;
        if (hasStaticResources) {
            publicAPI[genericResources] = getGenericResources;
        }
        publicAPI[removeWidgetResource] = removeWidget;
        return publicAPI;
    };

    return getPublicAPI();
};
